const translationModuleLoaders = import.meta.glob<Record<string, Record<string, string>>>('./*/*.ts');

const eagerDefaultModules = import.meta.glob<Record<string, Record<string, string>>>(
  './it/*.ts',
  { eager: true }
);
const eagerFallbackModules = import.meta.glob<Record<string, Record<string, string>>>(
  './en/*.ts',
  { eager: true }
);

function isTranslationRecord(value: unknown): value is Record<string, string> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  return Object.values(value as Record<string, unknown>).every((entry) => typeof entry === 'string');
}

export const translations: Record<string, Record<string, string>> = {};

function mergeEagerModules(
  languageCode: string,
  modules: Record<string, Record<string, Record<string, string>>>
): void {
  const merged: Record<string, string> = {};
  for (const moduleValue of Object.values(modules)) {
    const entry = Object.values(moduleValue).find((candidate) => isTranslationRecord(candidate));
    if (entry) Object.assign(merged, entry);
  }
  if (Object.keys(merged).length > 0) {
    translations[languageCode] = Object.assign({}, translations[languageCode] ?? {}, merged);
  }
}

mergeEagerModules('it', eagerDefaultModules);
mergeEagerModules('en', eagerFallbackModules);

const loadingByModulePath = new Map<string, Promise<Record<string, string>>>();

function getLanguageModulePaths(languageCode: string): string[] {
  return Object.keys(translationModuleLoaders)
    .filter((modulePath) => modulePath.startsWith(`./${languageCode}/`))
    .sort();
}

function getModuleNameFromPath(modulePath: string): string {
  return modulePath.replace(/^\.\/[a-z-]+\//, '').replace(/\.ts$/, '');
}

function toModulePath(languageCode: string, moduleName: string): string {
  return `./${languageCode}/${moduleName}.ts`;
}

async function loadTranslationModule(modulePath: string): Promise<Record<string, string>> {
  if (loadingByModulePath.has(modulePath)) {
    return loadingByModulePath.get(modulePath)!;
  }

  const loader = translationModuleLoaders[modulePath];
  if (!loader) return {};

  const loadingPromise = (async () => {
    const moduleValue = await loader();
    const translationEntry = Object.values(moduleValue).find((entry) => isTranslationRecord(entry));
    return translationEntry ?? {};
  })();

  loadingByModulePath.set(modulePath, loadingPromise);

  const result = await loadingPromise;
  loadingByModulePath.delete(modulePath);
  return result;
}

export function getLoadedTranslations(languageCode: string): Record<string, string> | undefined {
  return translations[languageCode];
}

export function getAvailableTranslationModules(languageCode: string): string[] {
  return getLanguageModulePaths(languageCode).map(getModuleNameFromPath);
}

export async function loadLanguageTranslations(languageCode: string, moduleNames?: string[]): Promise<Record<string, string>> {
  const targetModulePaths =
    moduleNames && moduleNames.length > 0
      ? Array.from(new Set(moduleNames.map((moduleName) => toModulePath(languageCode, moduleName)))).filter(
          (modulePath) => Boolean(translationModuleLoaders[modulePath])
        )
      : getLanguageModulePaths(languageCode);

  if (targetModulePaths.length === 0) {
    translations[languageCode] = translations[languageCode] ?? {};
    return translations[languageCode];
  }

  const loadedParts = await Promise.all(targetModulePaths.map((modulePath) => loadTranslationModule(modulePath)));

  translations[languageCode] = Object.assign({}, translations[languageCode] ?? {}, ...loadedParts);
  return translations[languageCode];
}
